import "./ExerciseHRChart.css";
import { scaleLinear, min, max, extent, line, area, curveCatmullRom } from "d3";
import { useEffect, useMemo, useRef, useState } from "react";
import { FavoriteOutlined, Timer } from "@mui/icons-material";
import { useBrand } from "../context/BrandContext";
import { trainingHrDebugLog } from "../utils/trainingHrDebugLog";
import {
  getHrChartYDomain,
  getZoneChartBands,
  ZONE_BAND_OPACITY,
  HR_CURVE_STROKE,
} from "../features/trainingSummary/zoneUtils";

/** Backend sends graph time as "HH:mm:ss" elapsed or numeric seconds. */
function parseGraphTimeToSeconds(time) {
  if (time == null) return NaN;
  if (typeof time === "number" && Number.isFinite(time)) return time;
  const s = String(time).trim();
  if (/^\d+(\.\d+)?$/.test(s)) return Number(s);
  const parts = s.split(":").map((p) => parseInt(p, 10));
  if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return NaN;
}

function formatElapsed(sec) {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const s = Math.floor(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/** Seconds on chart x-axis: prefer positive recorded_sec, else offset from first recorded_at. */
function readingChartTimeSec(row, anchorMs) {
  const rs = Number(row.recorded_sec);
  if (Number.isFinite(rs) && rs > 0) return rs;
  if (Number.isFinite(anchorMs) && row.recorded_at) {
    const t = new Date(row.recorded_at).getTime();
    if (Number.isFinite(t)) return Math.max(0, Math.round((t - anchorMs) / 1000));
  }
  return Number.isFinite(rs) ? rs : 0;
}

function buildHrSeries(info) {
  if (!info) {
    trainingHrDebugLog("ExerciseHRChart buildHrSeries", { branch: "no info" });
    return { points: [], durationSec: 0 };
  }

  const readings = info.readings;
  if (Array.isArray(readings) && readings.length > 0) {
    const sorted = [...readings].sort((a, b) => {
      const as = Number(a.recorded_sec) || 0;
      const bs = Number(b.recorded_sec) || 0;
      if (as !== bs) return as - bs;
      const ta = a.recorded_at ? new Date(a.recorded_at).getTime() : 0;
      const tb = b.recorded_at ? new Date(b.recorded_at).getTime() : 0;
      return ta - tb;
    });
    const anchorMs = sorted[0]?.recorded_at
      ? new Date(sorted[0].recorded_at).getTime()
      : NaN;
    const points = sorted
      .map((d) => ({
        time: readingChartTimeSec(d, anchorMs),
        hr_value: Number(d.hr_value) || 0,
      }))
      .filter((d) => d.hr_value > 0 && Number.isFinite(d.time));
    const times = points.map((p) => p.time);
    let durationSec =
      times.length >= 2 ? Math.max(...times) - Math.min(...times) : 0;
    if (durationSec === 0 && sorted.length >= 2) {
      const w0 = sorted[0]?.recorded_at
        ? new Date(sorted[0].recorded_at).getTime()
        : NaN;
      const w1 = sorted[sorted.length - 1]?.recorded_at
        ? new Date(sorted[sorted.length - 1].recorded_at).getTime()
        : NaN;
      if (Number.isFinite(w0) && Number.isFinite(w1) && w1 >= w0) {
        durationSec = Math.round((w1 - w0) / 1000);
      }
    }
    trainingHrDebugLog("ExerciseHRChart buildHrSeries", {
      branch: "readings",
      sortedCount: sorted.length,
      pointsAfterFilter: points.length,
      durationSec,
      firstSample: sorted[0],
      lastSample: sorted[sorted.length - 1],
    });
    return { points, durationSec };
  }

  const graph = info.hrGraph;
  if (Array.isArray(graph) && graph.length > 0) {
    const points = [];
    let skippedInvalidTime = 0;
    let skippedNonPositiveHr = 0;
    for (const d of graph) {
      const t = parseGraphTimeToSeconds(d.time);
      const hr = Number(d.hr_value) || 0;
      if (!Number.isFinite(t) || t < 0) {
        skippedInvalidTime += 1;
        continue;
      }
      if (hr <= 0) {
        skippedNonPositiveHr += 1;
        continue;
      }
      points.push({ time: t, hr_value: hr });
    }
    points.sort((a, b) => a.time - b.time);
    const durationSec =
      points.length > 1 ? points[points.length - 1].time - points[0].time : 0;
    trainingHrDebugLog("ExerciseHRChart buildHrSeries", {
      branch: "hrGraph",
      graphRowCount: graph.length,
      pointsAfterFilter: points.length,
      skippedInvalidTime,
      skippedNonPositiveHr,
      durationSec,
      graphSample: graph.slice(0, 4),
    });
    return { points, durationSec };
  }

  trainingHrDebugLog("ExerciseHRChart buildHrSeries", {
    branch: "empty",
    readingsLen: Array.isArray(readings) ? readings.length : null,
    graphLen: Array.isArray(graph) ? graph.length : null,
    summary_duration_sec: info.summary_duration_sec,
  });
  return { points: [], durationSec: 0 };
}

function shortZoneLabel(zone) {
  switch (zone) {
    case 5:
      return "Z5 Peak";
    case 4:
      return "Z4 Hard";
    case 3:
      return "Z3 Aerobic";
    case 2:
      return "Z2 Fat Burn";
    case 1:
      return "Z1 Light";
    default:
      return "Z0 Inactive";
  }
}

function HRChart({ info, accent = "#0a7ebd", labels = {}, mhr = 187, compact = false, forceDark = false }) {
  const { themeMode } = useBrand();
  const isDark = forceDark || themeMode === "dark";

  const { points: data, durationSec } = useMemo(() => buildHrSeries(info), [info]);
  const durationLabel = formatElapsed(durationSec);

  const hasAggregates =
    info &&
    (Number(info.averageHR) > 0 ||
      Number(info.minHR) > 0 ||
      Number(info.maxHR) > 0);

  const hasChart = data.length > 0;
  const shouldRender = !!(info && (hasAggregates || hasChart));

  const [val, setVal] = useState(0);
  const [curHr, setCurHr] = useState(0);
  const [cirPos, setCirPos] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 360, height: 260 });
  const chartPanelRef = useRef(null);

  const tDuration = labels.duration || "Duration";
  const tAvg = labels.hrAvg || "Avg HR";
  const tMin = labels.hrMin || "Min HR";
  const tMax = labels.hrMax || "Max HR";
  const tHeading = labels.heading || "Heart rate";
  const tTimeAxis = labels.timeAxis || "Time";

  const [yMin, yMax] = useMemo(() => getHrChartYDomain(mhr), [mhr]);
  const zoneBands = useMemo(() => getZoneChartBands(mhr, isDark), [mhr, isDark]);

  useEffect(() => {
    const el = chartPanelRef.current;
    if (!el || !hasChart) return undefined;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({
          width: Math.max(width, 200),
          height: Math.max(height, 180),
        });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasChart]);

  useEffect(() => {
    if (!data.length) return;
    const t0 = min(data, (d) => d.time);
    setVal(t0);
    setCirPos(null);
    setCurHr(0);
  }, [data]);

  const peakPoint = useMemo(() => {
    if (!data?.length) return null;
    let maxPt = data[0];
    for (const d of data) {
      if (d.hr_value > maxPt.hr_value) maxPt = d;
    }
    return maxPt;
  }, [data]);

  const axisFill = isDark ? "rgba(255,255,255,0.85)" : "#0f172a";
  const gridStroke = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.10)";
  const zoneLabelFill = isDark
    ? "rgba(255,255,255,0.72)"
    : "rgba(30, 41, 59, 0.72)";
  const peakRing = isDark ? "#FFFFFF" : "#FFFFFF";

  if (!shouldRender) return null;

  const margin = { top: 24, left: 44, right: 16, bottom: 36 };
  const innerW =
    dimensions.width <= 425
      ? dimensions.width - 48
      : dimensions.width - margin.left - margin.right;
  const width = Math.max(innerW, 200);
  const height = Math.max(dimensions.height - margin.top - margin.bottom, 120);

  const timeExtent = data.length ? extent(data, (d) => d.time) : [0, 1];
  const xDomain =
    data.length && timeExtent[0] === timeExtent[1]
      ? [timeExtent[0] - 1, timeExtent[1] + 1]
      : timeExtent;
  const xAxis = scaleLinear().range([0, width]).domain(xDomain).nice();
  const yAxisLeft = scaleLinear().range([height, 0]).domain([yMin, yMax]);

  const getBandCoordinates = (low, high) => {
    const topVal = Math.min(yMax, high);
    const bottomVal = Math.max(yMin, low);
    if (topVal <= bottomVal) return null;
    const y = yAxisLeft(topVal);
    const h = yAxisLeft(bottomVal) - y;
    return { y, height: Math.max(0, h) };
  };

  const peakCoordinates = peakPoint
    ? {
        cx: xAxis(peakPoint.time),
        cy: yAxisLeft(
          Math.min(yMax, Math.max(yMin, peakPoint.hr_value))
        ),
        value: peakPoint.hr_value,
      }
    : null;

  let xTicks = xAxis.ticks(6);
  if (width <= 425 && data.length > 1) {
    const midTime = data[Math.floor(data.length / 2)].time;
    xTicks = [0, midTime, max(data, (d) => d.time)].filter(
      (v, i, a) => a.indexOf(v) === i
    );
  }

  const yTicks = zoneBands
    .flatMap((z) => [z.y1, z.y2])
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .sort((a, b) => a - b);

  const lineGen = line()
    .x((d) => xAxis(d.time))
    .y((d) =>
      yAxisLeft(Math.min(yMax, Math.max(yMin, d.hr_value)))
    )
    .curve(curveCatmullRom.alpha(0.5));

  const areaGen = area()
    .x((d) => xAxis(d.time))
    .y0(height)
    .y1((d) =>
      yAxisLeft(Math.min(yMax, Math.max(yMin, d.hr_value)))
    )
    .curve(curveCatmullRom.alpha(0.5));

  const linepath = data.length >= 2 ? lineGen(data) : null;
  const areapath = data.length >= 2 ? areaGen(data) : null;

  const handleInputChange = (event) => {
    if (!data.length) return;
    const next = parseInt(event.target.value, 10);
    const curItem = data.filter((item) => item.time <= next);
    if (!curItem.length) return;
    const last = curItem[curItem.length - 1];
    const hr_value = last.hr_value;
    const time = last.time;
    setVal(next);
    setCurHr(hr_value);
    setCirPos({
      cx: xAxis(time),
      cy: yAxisLeft(Math.min(yMax, Math.max(yMin, hr_value))),
    });
  };

  const sliderMin = data.length ? min(data, (d) => d.time) : 0;
  const sliderMax = data.length ? max(data, (d) => d.time) : 0;
  const fillGradientId = compact ? "exHrFillGradChat" : "exHrFillGrad";
  const glowFilterId = compact ? "exHrGlowFilterChat" : "exHrGlowFilter";

  return (
    <div className={`hr-chart-root${isDark ? " hr-chart-root--dark" : ""}${compact ? " hr-chart-root--compact" : ""}`}>
      <section className="hr-summary-section" style={{ "--hr-accent": accent }}>
        <h3 className="hr-summary-heading">{tHeading}</h3>
        <div className="hr-summary-cards">
          <div className="hr-summary-card">
            <div className="hr-summary-card__icon" aria-hidden>
              <Timer />
            </div>
            <div className="hr-summary-card__value">{durationLabel}</div>
            <div className="hr-summary-card__label">{tDuration}</div>
          </div>
          {Number(info?.averageHR) > 0 && (
            <div className="hr-summary-card">
              <div className="hr-summary-card__icon hr-summary-card__icon--tag" aria-hidden>
                <span>AVG</span>
                <FavoriteOutlined />
              </div>
              <div className="hr-summary-card__value">
                {Math.round(Number(info.averageHR))}{" "}
                <span className="hr-summary-card__unit">bpm</span>
              </div>
              <div className="hr-summary-card__label">{tAvg}</div>
            </div>
          )}
          {Number(info?.minHR) > 0 && (
            <div className="hr-summary-card">
              <div className="hr-summary-card__icon hr-summary-card__icon--tag" aria-hidden>
                <span>MIN</span>
                <FavoriteOutlined />
              </div>
              <div className="hr-summary-card__value">
                {info.minHR} <span className="hr-summary-card__unit">bpm</span>
              </div>
              <div className="hr-summary-card__label">{tMin}</div>
            </div>
          )}
          {Number(info?.maxHR) > 0 && (
            <div className="hr-summary-card">
              <div className="hr-summary-card__icon hr-summary-card__icon--tag" aria-hidden>
                <span>MAX</span>
                <FavoriteOutlined />
              </div>
              <div className="hr-summary-card__value">
                {info.maxHR} <span className="hr-summary-card__unit">bpm</span>
              </div>
              <div className="hr-summary-card__label">{tMax}</div>
            </div>
          )}
        </div>
      </section>

      {hasChart && (
        <>
          <div className="hr-chart-panel-wrap">
            <div className="hr-chart-y-label">bpm</div>
            <div
              className="hr-chart-canvas"
              ref={chartPanelRef}
              style={{
                height: compact ? "168px" : dimensions.width <= 520 ? "340px" : "400px",
              }}
            >
              <svg
                width={width + margin.left + margin.right}
                height={height + margin.top + margin.bottom}
              >
                <defs>
                  <filter id={glowFilterId} x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="#FF334B" floodOpacity="0.9" />
                    <feDropShadow dx="0" dy="0" stdDeviation="5" floodColor="#FF334B" floodOpacity="0.55" />
                    <feDropShadow dx="0" dy="0" stdDeviation="10" floodColor="#FF334B" floodOpacity="0.25" />
                  </filter>
                  <linearGradient id={fillGradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={HR_CURVE_STROKE} stopOpacity={isDark ? 0.40 : 0.32} />
                    <stop offset="60%" stopColor={HR_CURVE_STROKE} stopOpacity={isDark ? 0.12 : 0.08} />
                    <stop offset="100%" stopColor={HR_CURVE_STROKE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <g transform={`translate(${margin.left},${margin.top})`}>
                  {zoneBands.map((zone) => {
                    const coords = getBandCoordinates(zone.y1, zone.y2);
                    if (!coords) return null;
                    return (
                      <g key={`zone-bg-${zone.zone}`}>
                        <rect
                          x={0}
                          y={coords.y}
                          width={width}
                          height={coords.height}
                          fill={zone.color}
                          opacity={zone.fillOpacity ?? ZONE_BAND_OPACITY}
                        />
                        <line
                          x1={0}
                          y1={coords.y}
                          x2={width}
                          y2={coords.y}
                          stroke={zone.borderColor || (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)")}
                          strokeWidth={1}
                        />
                      </g>
                    );
                  })}

                  {yTicks.map((bpm) => {
                    if (bpm <= yMin || bpm >= yMax) return null;
                    const yVal = yAxisLeft(bpm);
                    return (
                      <line
                        key={`grid-${bpm}`}
                        x1={0}
                        y1={yVal}
                        x2={width}
                        y2={yVal}
                        stroke={gridStroke}
                        strokeWidth={0.8}
                        strokeDasharray="4,4"
                      />
                    );
                  })}

                  {xTicks.map((tick) => (
                    <g
                      key={String(tick)}
                      transform={`translate(${xAxis(tick)},${height + 18})`}
                    >
                      <text
                        dy=".71em"
                        textAnchor="middle"
                        className="hr-chart-tick"
                        fill={axisFill}
                      >
                        {formatElapsed(tick)}
                      </text>
                    </g>
                  ))}
                  <text
                    x={width / 2}
                    y={height + 42}
                    textAnchor="middle"
                    className="hr-chart-axis-title"
                    fill={axisFill}
                  >
                    {tTimeAxis}
                  </text>

                  {yTicks.map((tick) => (
                    <g key={`y-${tick}`} transform={`translate(-8,${yAxisLeft(tick)})`}>
                      <text
                        textAnchor="end"
                        className="hr-chart-tick hr-chart-tick--y"
                        fill={axisFill}
                      >
                        {tick}
                      </text>
                    </g>
                  ))}

                  {areapath && (
                    <path d={areapath} fill={`url(#${fillGradientId})`} />
                  )}

                  {cirPos && (
                    <>
                      <line
                        x1={cirPos.cx}
                        y1={cirPos.cy}
                        x2={cirPos.cx}
                        y2={height}
                        stroke={HR_CURVE_STROKE}
                        strokeWidth={1.5}
                        opacity={0.85}
                      />
                      <circle
                        cx={cirPos.cx}
                        cy={cirPos.cy}
                        r={6}
                        fill={HR_CURVE_STROKE}
                        stroke={peakRing}
                        strokeWidth={2}
                      />
                    </>
                  )}

                  {linepath && (
                    <path
                      className="hr-chart-line"
                      d={linepath}
                      stroke={HR_CURVE_STROKE}
                      fill="none"
                      strokeWidth={3.2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      filter={`url(#${glowFilterId})`}
                    />
                  )}

                  {data.length === 1 && (
                    <circle
                      cx={xAxis(data[0].time)}
                      cy={yAxisLeft(
                        Math.min(yMax, Math.max(yMin, data[0].hr_value))
                      )}
                      r={6}
                      fill={HR_CURVE_STROKE}
                      stroke={peakRing}
                      strokeWidth={2}
                    />
                  )}

                  {peakCoordinates && (
                    <g key="peak-hr-indicator">
                      <circle
                        cx={peakCoordinates.cx}
                        cy={peakCoordinates.cy}
                        r={11}
                        fill="rgba(255, 51, 75, 0.25)"
                        className="hr-pulse-circle"
                        style={{
                          transformOrigin: `${peakCoordinates.cx}px ${peakCoordinates.cy}px`,
                        }}
                      />
                      <circle
                        cx={peakCoordinates.cx}
                        cy={peakCoordinates.cy}
                        r={7.5}
                        fill="rgba(255, 51, 75, 0.85)"
                        filter={`url(#${glowFilterId})`}
                      />
                      <circle
                        cx={peakCoordinates.cx}
                        cy={peakCoordinates.cy}
                        r={4.5}
                        fill={HR_CURVE_STROKE}
                        stroke={peakRing}
                        strokeWidth={2}
                      />
                    </g>
                  )}
                </g>
              </svg>
            </div>
          </div>

          {data.length >= 2 && !compact && (
            <div
              className="hr-chart-slider"
              style={{ maxWidth: width + margin.left + margin.right }}
            >
              <input
                type="range"
                className="hr-chart-slider__input"
                style={{ accentColor: HR_CURVE_STROKE }}
                min={sliderMin}
                max={sliderMax}
                onChange={handleInputChange}
                value={val}
                step={1}
              />
              <div className="hr-chart-slider__readout">
                {curHr > 0 && <strong>{curHr} bpm</strong>}
                {curHr > 0 && <span> · </span>}
                <span>{formatElapsed(val)}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default HRChart;
